import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import IconButton from '@mui/material/IconButton';
import { Component, createRef, Fragment } from "react";
import { CollapsibleContainer } from "./Container";
import { rewriteVulgarFraction } from "./PlateCalculator";
import "./PlatePicker.css";
import Units, { unitToString } from "./Units";


const lbsPlates = [100, 55, 45, 35, 25, 15, 10, 5, 2.5, 1.25, 1, 0.75, 0.5, 0.25];

const kgsPlates = [50, 25, 20, 15, 10, 5, 2.5, 2, 1.5, 1.25, 1, 0.75, 0.5, 0.25, 0.125];
const canvasSide = 56;

function inputValueTransform(value) {
  if (value === 0) {
    return "";
  }
  return value.toString();
}

const platingCollapsedKey = "plating-collapse"

class PlatePicker extends Component {
  barbellWeight = () => {
    return this.props.unit === Units.Kg ? this.props.plating.kgsBarbellWeight : this.props.plating.lbsBarbellWeight;
  }

  platesList = () => {
    return this.props.unit === Units.Kg ? kgsPlates : lbsPlates;
  }

  plates = () => {
    return this.props.unit === Units.Kg ? this.props.plating.kgsPlates : this.props.plating.lbsPlates;
  }

  sumWeights = () => {
    return this.platesList().reduce((total, plateWeight) => {
      return total + plateWeight * this.plates()[plateWeight.toString()] * 2;
    }, this.barbellWeight());
  }

  onBarbellWeightChange = (event) => {
    let newWeight = Number.parseFloat(event.target.value);
    if (isNaN(newWeight) || newWeight < 0) {
      newWeight = 0;
    }

    if (this.props.unit === Units.Kg) {
      this.platingChanged({ kgsBarbellWeight: newWeight });
    } else {
      this.platingChanged({ lbsBarbellWeight: newWeight });
    }
  }

  platingChanged = (platingUpdate) => {
    const copy = { ...this.props.plating };
    this.props.platingChanged?.(Object.assign(copy, platingUpdate));
  }

  render() {
    let pickers = this.platesList().map((pickerWeight) => {
      let onChange = (newCount) => {
        let plates = {};

        this.platesList().forEach((weight) => {
          if (weight === pickerWeight) {
            plates[weight.toString()] = newCount;
          } else {
            plates[weight.toString()] = this.plates()[weight.toString()];
          }
        });

        if (this.props.unit === Units.Kg) {
          this.platingChanged({ kgsPlates: plates });
        } else {
          this.platingChanged({ lbsPlates: plates });
        }
      };

      return <SinglePlatePicker
        key={pickerWeight.toString() + unitToString(this.props.unit)}
        weight={pickerWeight}
        value={this.plates()[pickerWeight.toString()] ?? 0}
        onChange={onChange}
        unit={this.props.unit}></SinglePlatePicker>
    });

    return <CollapsibleContainer title="Plating & Barbell" className="large" storageKey={platingCollapsedKey}>
      <Fragment>
        <div className="plate-pickers">
          {pickers}
        </div>
        <hr className="divider" />
        <div className="barbell-and-total">
          <label className="barbell-weight">
            Barbell weight: <input type="number" inputMode="decimal" value={inputValueTransform(this.barbellWeight())} onChange={this.onBarbellWeightChange} />

            {unitToString(this.props.unit)}
          </label>
          <div className="total-weight">
            Maximum total weight: <span className="actual-total">{this.sumWeights()}</span>
            {unitToString(this.props.unit)}
          </div>
        </div></Fragment>
    </CollapsibleContainer>;
  }
}

const lbsRatios = {
  "100": 1.0,
  "55": 1.0,
  "45": 1.0,
  "35": 0.975,
  "25": 0.95,
  "15": 0.925,
  "10": 0.9,
  "5": 0.8,
  "2.5": 0.7,
  "1.25": 0.675,
  "1": 0.675,
  "0.75": 0.675,
  "0.5": 0.675,
  "0.25": 0.675,
};

const kgsRatios = {
  "50": 1.0,
  "25": 1.0,
  "20": 1.0,
  "15": 0.975,
  "10": 0.95,
  "5": 0.9,
  "2.5": 0.8,
  "2": 0.775,
  "1.5": 0.75,
  "1.25": 0.7,
  "1": 0.7,
  "0.75": 0.675,
  "0.5": 0.675,
  "0.25": 0.675,
  "0.125": 0.675,
};

function ratiosForUnit(unit) {
  return unit === Units.Kg ? kgsRatios : lbsRatios;
}

class SinglePlateIcon extends Component {
  constructor(props) {
    super(props);
    this.canvasRef = createRef();
    this.ratio = ratiosForUnit(this.props.unit)[this.props.weight.toString()];

    this.hasLines = true;
    if (this.ratio <= 0.925) {
      this.hasLines = false;
    }

    this.fourLabels = true;
    if (this.ratio < 0.9) {
      this.fourLabels = false;
    }
  }

  componentDidMount() {
    const canvas = this.canvasRef.current;

    /** @type {CanvasRenderingContext2D} */
    const ctx = canvas.getContext("2d");

    // ensure all drawing operations are scaled
    ctx.scale(devicePixelRatio, devicePixelRatio);

    // We're scaling all the plates to a ratio according to their weight. Since we
    // want the plates to touch the bottom, we have to adjust the middle for it.
    const radius = (canvasSide / 2 - 2) * this.ratio;
    const middleY = canvasSide - canvasSide * this.ratio / 2;
    const middleX = canvasSide / 2;

    // This will enable us to come back to a clean slate easily.
    ctx.save();

    // Since we're going to draw things is a circle. It helps a lot to be centered as it
    // makes the rotations easier.
    ctx.translate(middleX, middleY);

    // This draws the outer ring.
    ctx.strokeStyle = "#C8C8C8";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(0, 0, radius - 2, 0, 2 * Math.PI);
    ctx.stroke();

    // This fills up the circle.
    ctx.fillStyle = "#1E1E1E";
    ctx.fill();

    ctx.font = "bold 9px sans-serif";
    ctx.fillStyle = "#FFFFFF";
    const weightString = rewriteVulgarFraction(this.props.weight.toString());

    // We'll need the text width to center the weight.
    const textMeasure = ctx.measureText(weightString);
    const textWidth = textMeasure.width;

    // And the height since placing it from the top-left corner is easier because we're scaling
    // the circle. fillText offset it for the bottom-left, which doesn't scale well because we
    // don't scale the text size.
    const textHeight = textMeasure.actualBoundingBoxAscent;
    const textVerticalOffset = -5 / 7;

    ctx.lineWidth = 1;

    // Now, we're going to rotate four times, each time writing the weight and drawing the
    // lines (if needed).
    for (let i = 0; i < 4; i++) {
      if (this.fourLabels || i % 2 === 0) {
        ctx.fillText(weightString, -textWidth / 2, textVerticalOffset * radius + textHeight);
      }

      // Rotate an eight of a turn and draw the line if needed.
      ctx.rotate(Math.PI / 4);
      if (this.hasLines) {
        ctx.beginPath();
        ctx.moveTo(0, -radius / 5);
        ctx.lineTo(0, -7 * radius / 10);
        ctx.stroke();
      }

      // Rotate another eight so that we've done a full quarter rotation.
      ctx.rotate(Math.PI / 4);
    }

    // And we leave things as we found them.
    ctx.restore();
  }

  render() {
    return <div className="plate-icon">
      <canvas ref={this.canvasRef} width={canvasSide * devicePixelRatio} height={canvasSide * devicePixelRatio} />
      {this.props.weight} {unitToString(this.props.unit)}
    </div>;
  }
}

const maxPlateCount = 12;

class SinglePlatePicker extends Component {
  constructor(props) {
    super(props);

    this.inputRef = createRef();
  }

  inputChanged = (event) => {
    let node = event.target;
    let value = node.value;

    let parsedValue = Number.parseInt(value);

    if (value === "") {
      parsedValue = 0;
    }

    if (isNaN(parsedValue)) {
      if (this.props.value === 0) {
        event.target.value = "";
      } else {
        event.target.value = this.props.value.toString();
      }
    } else {
      if (parsedValue > maxPlateCount) {
        event.target.value = maxPlateCount.toString();
        parsedValue = maxPlateCount;
      } else {
        this.setInputValue(parsedValue);
      }

      if (parsedValue !== this.props.value) {
        this.props.onChange?.(parsedValue);
      }
    }
  }

  setInputValue = (value) => {
    this.inputRef.current.value = inputValueTransform(value);

  }

  incrementPlateCount = () => {
    let newValue = this.props.value + 1;

    this.setInputValue(newValue);
    this.props.onChange?.(newValue);
  }

  decrementPlateCount = () => {
    let newValue = this.props.value - 1;

    this.setInputValue(newValue);
    this.props.onChange?.(newValue);
  }

  disablePlusButton = () => {
    return this.props.value >= maxPlateCount;
  }

  render() {
    return <div className="single-picker">
      <SinglePlateIcon weight={this.props.weight} unit={this.props.unit}></SinglePlateIcon>
      <div className="count-control">
        <IconButton disableRipple tabIndex={-1} aria-hidden="true" disabled={!this.props.value} onClick={this.decrementPlateCount}>
          <IndeterminateCheckBoxIcon />
        </IconButton>
        <input ref={this.inputRef} type="text" inputMode="numeric" className="plate-count" placeholder="-" value={inputValueTransform(this.props.value)} onChange={this.inputChanged} />
        <IconButton disableRipple tabIndex={-1} aria-hidden="true" disabled={this.disablePlusButton()} onClick={this.incrementPlateCount}>
          <AddBoxIcon />
        </IconButton>
      </div>
    </div>
  }
}

export default PlatePicker;